"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import NextLink from "next/link";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import { useTranslation } from "react-i18next";

const OMNI_ORANGE = "#C2501F";
const STEP_ORANGE = "#E2622F";
const PURPLE = "#7A2BF5";
const ARROW_COLOR = "#F0A47A";

const arrowSx = {
  fontSize: 16,
  color: ARROW_COLOR,
  stroke: ARROW_COLOR,
  strokeWidth: 1.1, // bold effect
  flexShrink: 0,
};

const stepStyles = [
  { bg: "#FFFFFF", color: "#14122B", border: "1px solid #EDE6FA" },
  { bg: STEP_ORANGE, color: "#FFFFFF", border: `1px solid ${STEP_ORANGE}` },
  { bg: "#14122B", color: "#FFFFFF", border: "1px solid #14122B" },
];

const OmniHowItWorksSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const raw = t("omniHowItWorks.lanes", { returnObjects: true });
  const lanes = Array.isArray(raw) ? raw : [];
  const labelColors = [OMNI_ORANGE, PURPLE];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        backgroundColor: "#FFFFFF",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
          <Typography
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              fontWeight: 600,
              color: OMNI_ORANGE,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              fontFamily: "var(--font-outfit)",
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {t("omniHowItWorks.label")}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "24px", sm: "30px", md: "34px", lg: "38px" },
              fontWeight: 700,
              lineHeight: 1.25,
              color: "#14122B",
              fontFamily: "var(--font-plusjakarta)",
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            {t("omniHowItWorks.heading")}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "14px", md: "14.5px" },
              color: "#55556E",
              lineHeight: 1.8,
              fontWeight: 400,
              fontFamily: "var(--font-outfit)",
              maxWidth: 720,
              mx: "auto",
            }}
          >
            {t("omniHowItWorks.description")}
          </Typography>
        </Box>

        {/* Lanes */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mb: { xs: 4, md: 5 } }}>
          {lanes.map((lane, laneIndex) => (
            <Box
              key={laneIndex}
              sx={{
                backgroundColor: "#F6F2FF",
                border: "1px solid #EDE6FA",
                borderRadius: "16px",
                p: { xs: 2.5, md: 3 },
              }}
            >
              <Typography
                sx={{
                  fontSize: "9px",
                  fontWeight: 700,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  color: labelColors[laneIndex] || OMNI_ORANGE,
                  fontFamily: "var(--font-outfit)",
                  mb: 1.5,
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {lane.label}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  gap: { xs: 1, md: 1.5 },
                }}
              >
                {(lane.steps || []).map((step, i, arr) => {
                  const s = stepStyles[i] || stepStyles[0];
                  return (
                    <React.Fragment key={i}>
                      <Box
                        sx={{
                          flex: 1,
                          width: { xs: "100%", md: "auto" },
                          backgroundColor: s.bg,
                          border: s.border,
                          borderRadius: "10px",
                          px: 2.5,
                          py: { xs: 2, md: 2.25 },
                          textAlign: isArabic ? "right" : "left",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: "13px", md: "14px" },
                            fontWeight: 600,
                            color: s.color,
                            fontFamily: "var(--font-outfit)",
                          }}
                        >
                          {step}
                        </Typography>
                      </Box>

                      {i < arr.length - 1 && (
                        <>
                          <ArrowForwardRoundedIcon
                            sx={{
                              ...arrowSx,
                              display: { xs: "none", md: "block" },
                              transform: isArabic ? "scaleX(-1)" : "none",
                            }}
                          />
                          <ArrowDownwardRoundedIcon
                            sx={{
                              ...arrowSx,
                              display: { xs: "block", md: "none" },
                            }}
                          />
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>

        {/* CTA */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
            <Button
              component="a"
              sx={{
                bgcolor: "#000",
                color: "white",
                px: { xs: 3, sm: 3.5 },
                py: { xs: 1.2, sm: 1.4 },
                borderRadius: "50px",
                textTransform: "none",
                fontSize: { xs: "14px", sm: "15px" },
                fontWeight: 500,
                fontFamily: "var(--font-outfit)",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease-in-out",
                "&:hover": { bgcolor: "#C2501F", transform: "scale(1.03)" },
              }}
            >
              {t("omniHowItWorks.cta")}
            </Button>
          </NextLink>
        </Box>
      </Container>
    </Box>
  );
};

export default OmniHowItWorksSection;